import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

// Address book — customer admins manage delivery addresses on top of the
// SOS-on-file shipping address. Custom addresses are portal-only: they
// surface in the order confirmation email and as a "DELIVER TO:" line in
// the SOS sales order comment, but don't change the customer's SOS record.
export default function Addresses() {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // { id?, label, line1, ... } or null
  const [busy, setBusy] = useState(null);          // id mid-action (delete, set-default)

  // Legacy customers (no role on token) and customer_admins can manage. Other
  // roles get a read-only view (used for the picker in the basket — same data
  // source).
  const canManage = !user?.role || user?.role === 'customer_admin';

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await api.listAddresses();
      setAddresses(data.addresses || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setEditing({ label: '', line1: '', line2: '', city: '', state: '', zip: '', country: 'US', notes: '', isDefault: false });
  }
  function openEdit(a) {
    if (a.isReadOnly) return;
    setEditing({ ...a });
  }
  function closeEditor() { setEditing(null); }

  async function save() {
    if (!editing) return;
    const payload = {
      label:  editing.label,
      line1:  editing.line1,
      line2:  editing.line2,
      city:   editing.city,
      state:  editing.state,
      zip:    editing.zip,
      country: editing.country || 'US',
      notes:  editing.notes,
      isDefault: !!editing.isDefault,
    };
    try {
      if (editing.id) {
        await api.updateAddress(editing.id, payload);
      } else {
        await api.createAddress(payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    }
  }

  async function remove(a) {
    if (!window.confirm(`Remove "${a.label}"?`)) return;
    setBusy(a.id);
    try {
      await api.deleteAddress(a.id);
      await load();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }

  async function setDefault(a) {
    setBusy(a.id);
    try {
      await api.setDefaultAddress(a.id);
      await load();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div style={s.header}>
        <div style={s.titleRow}>
          <h2 style={s.title}>Delivery Addresses</h2>
          <span style={s.count}>{addresses.length}</span>
        </div>
        {canManage && (
          <button style={s.addBtn} onClick={openAdd}>+ Add Address</button>
        )}
      </div>

      <p style={s.hint}>
        Pick from these at checkout. Custom addresses are visible to your team and
        appear on the order confirmation; the warehouse is informed via the order
        notes. The "On file with Silver Service" entry is the address registered
        with us — to change that, contact us at orders@silverservice.com.
      </p>

      {error && <div style={s.error}>{error}</div>}
      {loading && <div style={s.muted}>Loading addresses…</div>}

      {!loading && (
        <div style={s.list}>
          {addresses.map(a => (
            <div key={a.id} style={{ ...s.card, ...(a.isDefault ? s.cardDefault : {}) }}>
              <div style={s.cardLeft}>
                <div style={s.labelRow}>
                  <div style={s.label}>{a.label}</div>
                  {a.isDefault && <span style={s.defaultPill}>Default</span>}
                  {a.isReadOnly && <span style={s.readOnlyPill}>From SOS</span>}
                </div>
                <div style={s.address}>
                  <div>{a.line1}{a.line2 ? `, ${a.line2}` : ''}</div>
                  <div>{[a.city, a.state].filter(Boolean).join(', ')} {a.zip}</div>
                  {a.notes && <div style={s.notes}>{a.notes}</div>}
                </div>
              </div>
              {canManage && (
                <div style={s.cardActions}>
                  {!a.isDefault && (
                    <button style={s.linkBtn} disabled={busy === a.id} onClick={() => setDefault(a)}>
                      Set default
                    </button>
                  )}
                  {!a.isReadOnly && (
                    <>
                      <button style={s.linkBtn} disabled={busy === a.id} onClick={() => openEdit(a)}>
                        Edit
                      </button>
                      <button style={{ ...s.linkBtn, color: '#c0392b' }} disabled={busy === a.id} onClick={() => remove(a)}>
                        Remove
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div style={s.modalBackdrop} onClick={closeEditor}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editing.id ? 'Edit Address' : 'Add Address'}</h3>

            <Field label="Label (e.g. Patio bar, Catering site)" value={editing.label}
              onChange={v => setEditing({ ...editing, label: v })} />
            <Field label="Street address" value={editing.line1}
              onChange={v => setEditing({ ...editing, line1: v })} />
            <Field label="Apartment / suite / unit (optional)" value={editing.line2}
              onChange={v => setEditing({ ...editing, line2: v })} />

            <div style={s.row}>
              <div style={{ flex: 2 }}>
                <Field label="City" value={editing.city}
                  onChange={v => setEditing({ ...editing, city: v })} />
              </div>
              <div style={{ flex: 1 }}>
                <Field label="State" value={editing.state}
                  onChange={v => setEditing({ ...editing, state: v.toUpperCase().slice(0, 2) })} />
              </div>
              <div style={{ flex: 1 }}>
                <Field label="ZIP" value={editing.zip}
                  onChange={v => setEditing({ ...editing, zip: v })} />
              </div>
            </div>

            <Field label="Delivery notes (optional, e.g. Use back loading dock)"
              value={editing.notes}
              onChange={v => setEditing({ ...editing, notes: v })} />

            <label style={s.checkboxRow}>
              <input type="checkbox"
                checked={!!editing.isDefault}
                onChange={e => setEditing({ ...editing, isDefault: e.target.checked })} />
              Make this the default address for new orders
            </label>

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeEditor}>Cancel</button>
              <button style={s.saveBtn} onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={s.input}
      />
    </label>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem', flexWrap: 'wrap' },
  titleRow: { display: 'flex', gap: '0.6rem', alignItems: 'center' },
  title: { fontSize: '1.4rem', fontWeight: 600, color: '#333', margin: 0 },
  count: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', background: '#eee9e2', padding: '0.2rem 0.65rem', borderRadius: 12 },
  hint: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', margin: '0 0 1.5rem', lineHeight: 1.5, maxWidth: 720 },
  addBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  error: { color: '#c0392b', fontSize: '0.85rem', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6, padding: '0.6rem 0.8rem', margin: '0 0 1rem' },
  muted: { fontSize: '0.9rem', color: 'rgba(0,0,0,0.45)' },

  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { background: '#fff', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', border: '1px solid transparent' },
  cardDefault: { border: '1px solid #A50B18', boxShadow: '0 1px 4px rgba(165,11,24,0.15)' },
  cardLeft: { flex: 1, minWidth: 0 },
  labelRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' },
  label: { fontSize: '1rem', fontWeight: 600, color: '#333' },
  defaultPill: { fontSize: '0.7rem', fontWeight: 600, color: '#A50B18', background: 'rgba(165,11,24,0.08)', padding: '0.15rem 0.55rem', borderRadius: 12 },
  readOnlyPill: { fontSize: '0.7rem', fontWeight: 600, color: 'rgba(0,0,0,0.5)', background: '#eee9e2', padding: '0.15rem 0.55rem', borderRadius: 12 },
  address: { fontSize: '0.9rem', color: '#444', lineHeight: 1.5 },
  notes: { color: 'rgba(0,0,0,0.55)', fontStyle: 'italic', marginTop: '0.25rem', fontSize: '0.85rem' },
  cardActions: { display: 'flex', gap: '0.6rem', flexShrink: 0 },
  linkBtn: { background: 'none', border: 'none', color: '#A50B18', fontSize: '0.85rem', cursor: 'pointer', padding: '0.2rem 0.4rem', fontWeight: 500 },

  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: 10, padding: '1.5rem', width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto' },
  modalTitle: { fontSize: '1.2rem', fontWeight: 600, margin: '0 0 1rem', color: '#333' },

  field: { display: 'block', marginBottom: '0.85rem' },
  fieldLabel: { display: 'block', fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', marginBottom: '0.3rem', fontWeight: 500 },
  input: { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #ddd', borderRadius: 5, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  row: { display: 'flex', gap: '0.6rem' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#444', margin: '0.5rem 0 1rem', cursor: 'pointer' },

  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' },
  cancelBtn: { background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' },
  saveBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
};
